import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormText,
} from 'reactstrap';
import { ingredientApiService } from 'services/ingredientApiService';

const CreateIngredientModal = ({
  isOpen,
  toggle,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    ingredient_name: '',
    price: '',
    quantity: '',
    unit: 'g',
    unitprice: '',
    store_name: '',
    note: '',
    ingredient_image: null,
  });
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');

  // Get logged in user info
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser") || '{}');

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = { ...formData, [name]: value };

    // Calculate unit price when price or quantity changes
    if (name === 'price' || name === 'quantity') {
      const price = name === 'price' ? value : formData.price;
      const quantity = name === 'quantity' ? value : formData.quantity;

      if (price && quantity) {
        const unitPrice = (parseFloat(price) / parseFloat(quantity)).toFixed(2);
        updatedData.unitprice = unitPrice;
      }
    }

    setFormData(updatedData);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        ingredient_image: file
      }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.ingredient_name || !formData.price || !formData.quantity) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const dataToSend = new FormData();
      dataToSend.append('ingredient_name', formData.ingredient_name);
      dataToSend.append('price', formData.price);
      dataToSend.append('quantity', formData.quantity);
      dataToSend.append('unit', formData.unit);
      dataToSend.append('unitprice', formData.unitprice || '');
      dataToSend.append('store_name', formData.store_name);
      dataToSend.append('note', formData.note);

      if (loggedInUser?.qb_user_id) {
        dataToSend.append('qb_admin_id', loggedInUser.qb_user_id);
      }

      if (loggedInUser?.id) {
        dataToSend.append('system_admin_id', loggedInUser.id);
      }

      if (formData.ingredient_image) {
        dataToSend.append('ingredient_image', formData.ingredient_image);
      }

      await ingredientApiService.createIngredient(dataToSend);

      onSuccess();

      // Reset form
      setFormData({
        ingredient_name: '',
        price: '',
        quantity: '',
        unit: 'g',
        unitprice: '',
        store_name: '',
        note: '',
        ingredient_image: null,
      });
      setImagePreview('');

    } catch (error) {
      console.error('Error creating ingredient:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Create New Ingredient</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <div className="row">
            <div className="col-md-6">
              <FormGroup>
                <Label for="ingredient_name">Ingredient Name *</Label>
                <Input
                  type="text"
                  id="ingredient_name"
                  name="ingredient_name"
                  value={formData.ingredient_name}
                  onChange={handleChange}
                  required
                />
              </FormGroup>



              <div className="row">
                <FormGroup>
                  <Label for="price">Price ($) *</Label>
                  <Input
                    type="number"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    required
                    step="0.01"
                    min="0"
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="unit">Unit</Label>
                  <Input
                    type="select"
                    id="unit"
                    name="unit"
                    value={formData.unit}
                    onChange={handleChange}
                  >
                    <option value="g">g</option>
                    <option value="kg">kg</option>
                    <option value="ml">ml</option>
                    <option value="L">L</option>
                    <option value="piece">piece</option>
                    <option value="oz">oz</option>
                    <option value="lb">lb</option>
                  </Input>
                </FormGroup>

                <FormGroup>
                  <Label for="note">Note</Label>
                  <Input
                    type="textarea"
                    id="note"
                    name="note"
                    value={formData.note}
                    onChange={handleChange}
                    rows="3"
                  />
                </FormGroup>

              </div>
            </div>

            <div className="col-md-6">
              <FormGroup>
                <Label for="store_name">Store Name</Label>
                <Input
                  type="text"
                  id="store_name"
                  name="store_name"
                  value={formData.store_name}
                  onChange={handleChange}
                />
              </FormGroup>

              <div className="col-md-12">
                <FormGroup>
                  <Label for="quantity">Quantity *</Label>
                  <Input
                    type="number"
                    id="quantity"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    required
                    step="0.01"
                    min="0"
                  />
                </FormGroup>
              </div>



              <FormGroup>
                <Label for="unitprice">Unit Price</Label>
                <Input
                  type="text"
                  id="unitprice"
                  name="unitprice"
                  value={formData.unitprice}
                  readOnly
                  className="bg-light"
                />
                <FormText>
                  Calculated automatically: Price / Quantity
                </FormText>
              </FormGroup>



              <FormGroup>
                <Label for="ingredient_image">Ingredient Image</Label>
                <Input
                  type="file"
                  id="ingredient_image"
                  name="ingredient_image"
                  onChange={handleFileChange}
                  accept="image/*"
                />

                {imagePreview && (
                  <div className="mt-2">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: '100px',
                        height: '100px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                      }}
                    />
                  </div>
                )}
              </FormGroup>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={loading}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1"></span>
                Creating...
              </>
            ) : (
              'Create Ingredient'
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CreateIngredientModal;